import type { PurchaseShipment } from "@/api/purchase";

const STEPS = [
  ["container", "1. Container"],
  ["packlist", "2. Packliste"],
  ["packing_review", "3. Packdaten"],
  ["transport", "4. Transport"],
  ["delivery_receipt", "5. Zustellung + WE"],
  ["storage", "6. Einlagerung"],
  ["complete", "7. Fertig"],
] as const;

export type ShipmentWorkflowView = NonNullable<PurchaseShipment["workflow"]>;

export function getShipmentWorkflow(shipment: PurchaseShipment): ShipmentWorkflowView {
  if (shipment.workflow) return shipment.workflow;

  const hasItems = shipment.items.length > 0;
  const packingComplete = hasItems && shipment.items.every((item) =>
    Boolean(item.carton_exception_reason)
      || (Number(item.cartons) > 0 && Number(item.pieces_per_carton) > 0),
  );
  const delivered = shipment.status === "zugestellt" || shipment.status === "im_wareneingang" || shipment.status === "eingelagert";
  const stored = shipment.status === "eingelagert";

  return {
    current_step: stored
      ? "complete"
      : delivered
        ? "storage"
        : !hasItems
          ? "packlist"
          : !packingComplete
            ? "packing_review"
            : "transport",
    blockers: !hasItems
      ? [{ code: "no_items", message: "Dem Container sind noch keine bestätigten Positionen zugeordnet." }]
      : !packingComplete
        ? [{ code: "packing_missing", message: "Kartons und Stück je Karton müssen geprüft werden." }]
        : [],
    // Ohne serverseitigen Packlistenbeleg darf ein altes/cached Payload niemals
    // eine Zustellung freischalten. Der Fallback ist absichtlich fail-closed.
    can_confirm_delivery: false,
    can_create_receipt: false,
    has_confirmed_packlist: false,
  };
}

export function ShipmentWorkflowStepper({ shipment }: { shipment: PurchaseShipment }) {
  const workflow = getShipmentWorkflow(shipment);
  const foundCurrent = STEPS.findIndex(([key]) => key === workflow.current_step);
  const current = foundCurrent >= 0 ? foundCurrent : null;
  return (
    <div className="mb-4 space-y-2" role="group" aria-label="Ablauf dieses Containers">
      <ol className="grid gap-1 sm:grid-cols-4 xl:grid-cols-7">
        {STEPS.map(([key, label], index) => (
          <li
            key={key}
            aria-current={index === current ? "step" : undefined}
            aria-label={`${label}: ${current === null ? "unbekannt" : index < current ? "erledigt" : index === current ? "aktuell" : "offen"}`}
            className={`rounded border px-2 py-1.5 text-[11px] ${
              current !== null && index < current
                ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"
                : current !== null && index === current
                  ? "border-primary bg-primary/10 font-semibold text-primary"
                  : "border-line bg-panel-2 text-ink-2"
            }`}
          >
            {label}
          </li>
        ))}
      </ol>
      {workflow.blockers.length > 0 ? (
        <div role="alert" className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-950/30 dark:text-red-200">
          <div className="font-semibold">Vor dem nächsten Schritt korrigieren:</div>
          <ul className="mt-1 list-disc pl-4">
            {workflow.blockers.map((blocker, index) => <li key={`${blocker.code}-${index}`}>{blocker.message}</li>)}
          </ul>
        </div>
      ) : null}
      {foundCurrent < 0 ? (
        <div role="alert" className="text-xs text-amber-700 dark:text-amber-300">
          Unbekannter Workflowstatus; die serverseitigen Freigaben bleiben maßgeblich. Bitte die Akte neu laden.
        </div>
      ) : null}
    </div>
  );
}
